import { useCallback, useRef, useState } from 'react';

import { ManagedFolderListResult } from '@the-real-insight/tri-model';

import { storageAPI } from '../../../StorageAPI';
import { TreeNode } from './Tree';

/**
 * Die Ablage als Baum: laden, im Takt halten, aufgeklappte Ordner behalten.
 *
 * Herausgelöst, weil zwei Ansichten denselben Baum über denselben Ablagen zeichnen — die
 * Dateiverwaltung und der Datenraum eines Projekts. Was sie unterscheidet, ist die Zeile (die eine
 * hängt Wissensbasen an, die andere den Zustand der Dokumentenanalyse) und was beim Anfassen
 * geschieht. Was sie teilen, ist alles hier: eine Auflistung, ihre Entprellung, ihr Wiederholen
 * nach einer Drosselung, und die Erinnerung daran, welche Ordner offen standen.
 *
 * Die letzten beiden Punkte sind der eigentliche Grund. Eine Neuauflistung baut den Baum neu; ohne
 * die gemerkten Zustände klappt bei jedem Hochladen alles zu, und ohne die Entprellung schickt ein
 * Ordner-Aufklappen drei Anfragen hintereinander.
 *
 * Nicht enthalten: was ein Knoten *ist* und was eine Aktion *tut*. Beides gehört der Ansicht.
 */

/** Wie lange nach einer Auflistung keine zweite losgeht — außer der Aufrufer besteht darauf. */
export const STORAGE_CALL_DEBOUNCE_MS = 1000;

/** Wie oft nach einer Drosselung erneut versucht wird, mit sich verdoppelnder Pause. */
const MAX_RATE_LIMIT_RETRIES = 3;

export interface StorageTreeOptions {
    customer: string | undefined;
    volume: string;
    /** Nur der Ast unter diesem Pfad — der Datenraum zeigt einen Projektordner, nicht das Volume. */
    basePath?: string;
    /** Aus der Auflistung den Baum bauen — hier entscheidet die Ansicht, wie eine Zeile aussieht. */
    buildNodes: (listing: ManagedFolderListResult) => TreeNode[];
    /** Wohin ein Fehler gemeldet wird, der den Leser angeht. Drosselungen gehören nicht dazu. */
    onError?: (error: any) => void;
}

/**
 * Die gemerkten Auf- und Zuklapp-Zustände auf einen frisch gebauten Baum legen.
 *
 * Rekursiv, weil die Erinnerung am Pfad hängt und Pfade auf jeder Ebene vorkommen. Ein Ordner, für
 * den nichts gemerkt ist, behält, was der Erbauer ihm mitgegeben hat.
 */
export const applyFolderStates = (
    nodes: TreeNode[],
    states: Map<string, boolean>,
): TreeNode[] =>
    (nodes ?? []).map((node) => {
        if (node.type !== 'folder' || !node.path) return node;

        return {
            ...node,
            isOpen: states.get(node.path) ?? node.isOpen ?? false,
            files: node.files ? applyFolderStates(node.files, states) : node.files,
        };
    });

export const useStorageTree = ({ customer, volume, basePath, buildNodes, onError }: StorageTreeOptions) => {
    const [data, setData] = useState<TreeNode[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [folderStates, setFolderStates] = useState<Map<string, boolean>>(new Map());

    /**
     * In Referenzen statt im Zustand.
     *
     * Beides wird *während* eines Laufs gelesen, nicht beim Zeichnen. Als Zustand gehalten läse ein
     * Lauf den Stand seines Renderdurchlaufs — die Entprellung ließe zwei gleichzeitige Anfragen
     * durch, und eine Auflistung legte veraltete Klappzustände auf.
     */
    const lastCallAt = useRef(0);
    const openStates = useRef(folderStates);

    const updateFolderState = useCallback((path: string, isOpen: boolean) => {
        setFolderStates((previous) => {
            const next = new Map(previous);
            next.set(path, isOpen);
            openStates.current = next;
            return next;
        });
    }, []);

    const buildRef = useRef(buildNodes);
    buildRef.current = buildNodes;

    const errorRef = useRef(onError);
    errorRef.current = onError;

    const refresh = useCallback(
        async (options: { force?: boolean; retry?: number } = {}): Promise<void> => {
            if (!customer) return;

            const now = Date.now();
            const { force = false, retry = 0 } = options;

            if (!force && now - lastCallAt.current < STORAGE_CALL_DEBOUNCE_MS) return;

            lastCallAt.current = now;

            try {
                const listing = await storageAPI.getStorage(customer, volume, basePath);

                setData(applyFolderStates(buildRef.current(listing), openStates.current));
                setIsInitialLoading(false);
            } catch (error: any) {
                /**
                 * Eine Drosselung ist kein Fehler des Lesers.
                 *
                 * Sie wird nicht gemeldet, sondern abgewartet — mit sich verdoppelnder Pause und
                 * dreimal. Erst danach bliebe die Ansicht leer, und dann wäre auch eine Meldung
                 * nicht falsch; bis dahin löst sich das von selbst.
                 */
                if (error?.message === 'Rate limited - too many requests') {
                    if (retry < MAX_RATE_LIMIT_RETRIES) {
                        setTimeout(
                            () => void refresh({ force: true, retry: retry + 1 }),
                            2 ** retry * 1000,
                        );
                    }
                    return;
                }

                setIsInitialLoading(false);
                errorRef.current?.(error);
            }
        },
        [customer, volume, basePath],
    );

    return { data, setData, isInitialLoading, folderStates, updateFolderState, refresh };
};
